/**
 * SubscriptionBillingSection — iter 82.7.
 *
 * Master/admin panel para:
 *   - Editar datos fiscales del emisor (ChefSync)
 *   - Emitir facturas mensuales de suscripción (F{YYYY}/{NNNNNN})
 *     mediante flujo en 2 pasos: Previsualizar → Confirmar (Emit/Gratuito/Excluir)
 *   - Ver el listado de facturas emitidas y descargar cualquiera en PDF
 *   - Widget de subidas asistidas del master (X/400 este mes)
 *
 * Se renderiza dentro de /admin justo antes de la tabla de Usuarios.
 */
import { useCallback, useEffect, useState } from "react";
import { FileText, Save, Send, Download, Loader2, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";

const CURRENT_MONTH = () => new Date().toISOString().slice(0, 7);
const fmtEur = (v) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

export default function SubscriptionBillingSection({ lang = "es", isMaster }) {
  const [issuer, setIssuer] = useState(null);
  const [savingIssuer, setSavingIssuer] = useState(false);
  const [month, setMonth] = useState(CURRENT_MONTH());
  const [invoices, setInvoices] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [issuing, setIssuing] = useState(false);
  // preview: array of preview rows, each augmented with UI state:
  //   { user_id, email, plan, list_price, base, vat_amount, total, prorate,
  //     ui_action: "emit"|"free"|"exclude", ui_base: string, ui_note: string }
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [assistUsage, setAssistUsage] = useState(null);

  const loadInvoices = useCallback(async (m) => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/admin/subscription-invoices?month=${m}`);
      setInvoices(data.invoices || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadAssistUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/master-assist-usage");
      setAssistUsage(data);
    } catch {
      /* master role required — silent if not applicable */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/issuer-fiscal");
        setIssuer(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
    loadInvoices(month);
    if (isMaster) loadAssistUsage();
  }, [loadInvoices, loadAssistUsage, month, isMaster]);

  const saveIssuer = async () => {
    if (!issuer) return;
    setSavingIssuer(true);
    try {
      await api.put("/admin/issuer-fiscal", issuer);
      toast.success(lang === "es" ? "Datos fiscales guardados" : "Issuer saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingIssuer(false);
    }
  };

  const runPreview = async () => {
    setIssuing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/subscription-invoices/generate", {
        month, dry_run: true,
      });
      const rows = (data.invoices || []).map((r) => ({
        ...r,
        ui_action: "emit",
        ui_base: String(r.base ?? 0),
        ui_note: "",
      }));
      setPreview({ ...data, rows });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIssuing(false);
    }
  };

  const updateRow = (userId, patch) => {
    setPreview((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((r) => (r.user_id === userId ? { ...r, ...patch } : r)),
      };
    });
  };

  const confirmEmit = async () => {
    if (!preview) return;
    const entries = preview.rows.map((r) => ({
      user_id: r.user_id,
      action: r.ui_action,
      base_override:
        r.ui_action === "emit" && Number(r.ui_base) !== Number(r.base)
          ? Number(r.ui_base)
          : null,
      note: r.ui_note || "",
    }));
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/subscription-invoices/confirm", {
        month,
        entries,
      });
      toast.success(
        lang === "es"
          ? `${data.emitted_count} emitidas · ${data.free_count} cortesía · ${data.excluded_count} excluidas`
          : `${data.emitted_count} issued · ${data.free_count} free · ${data.excluded_count} excluded`,
      );
      setPreview(null);
      await loadInvoices(month);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const resp = await api.get(`/billing/subscription-invoices/${inv.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(inv.number || "factura").replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section
      className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 space-y-6"
      data-testid="subscription-billing-section"
    >
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[var(--primary)]" />
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {lang === "es" ? "Facturación de suscripción" : "Subscription billing"}
          </h2>
        </div>
        {isMaster && assistUsage && (
          <div
            className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-[var(--border-color)] bg-[var(--surface-hover)]"
            data-testid="master-assist-usage-badge"
            title={lang === "es"
              ? "Facturas OCR subidas por Master en modo asistencia (impersonando tenants)"
              : "OCR uploads by Master while impersonating tenants"}
          >
            <ShieldCheck size={12} className="text-[var(--brand-accent)]" />
            <span className="text-[var(--text-secondary)]">
              {lang === "es" ? "Master asistencia" : "Master assist"}
            </span>
            <b className="font-mono-num">
              {assistUsage.used}/{assistUsage.cap}
            </b>
          </div>
        )}
      </div>

      {/* Issuer editor — solo master */}
      {isMaster && issuer && (
        <div className="rounded-md border border-[var(--border-color)] p-3">
          <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-3">
            {lang === "es" ? "Datos fiscales del emisor" : "Issuer fiscal data"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["name", lang === "es" ? "Razón social" : "Company name", "col-span-full"],
              ["tax_id", "NIF/CIF"],
              ["email", "Email"],
              ["address", lang === "es" ? "Dirección" : "Address", "col-span-full"],
              ["postal_code", "CP"],
              ["city", lang === "es" ? "Ciudad" : "City"],
              ["country", lang === "es" ? "País" : "Country"],
              ["iban", "IBAN"],
              ["footer_notes", lang === "es" ? "Notas al pie" : "Footer notes", "col-span-full"],
            ].map(([key, label, extra]) => (
              <div key={key} className={extra || ""}>
                <Label className="text-xs text-[var(--text-secondary)]">{label}</Label>
                <Input
                  value={issuer[key] || ""}
                  onChange={(e) => setIssuer({ ...issuer, [key]: e.target.value })}
                  data-testid={`issuer-${key}`}
                  className="mt-1 text-sm"
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              onClick={saveIssuer}
              disabled={savingIssuer}
              data-testid="issuer-save-btn"
              className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {savingIssuer ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1.5" />}
              {lang === "es" ? "Guardar emisor" : "Save issuer"}
            </Button>
          </div>
        </div>
      )}

      {/* Preview + Confirm */}
      <div className="space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs text-[var(--text-secondary)]">
              {lang === "es" ? "Mes a facturar" : "Billing month"}
            </Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setPreview(null); }}
              data-testid="billing-month-input"
              className="mt-1 w-40 text-sm"
            />
          </div>
          {isMaster && (
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={issuing}
              data-testid="billing-preview-btn"
            >
              {issuing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
              {lang === "es" ? "Previsualizar" : "Preview"}
            </Button>
          )}
          <p className="text-[11px] text-[var(--text-muted)] pb-1">
            {lang === "es"
              ? "Fecha de emisión = día 1 del mes. Primer mes se prorratea desde el alta."
              : "Issue date = day 1 of the billing month. First month is prorated from signup."}
          </p>
        </div>

        {/* --- Preview table with per-tenant actions --- */}
        {preview && (
          <div className="rounded-md border border-[var(--brand-accent)]/40 bg-[var(--brand-accent)]/5 p-3 space-y-2" data-testid="billing-preview-panel">
            <p className="text-sm font-medium">
              {lang === "es"
                ? `Previsualización de ${preview.month} — ${preview.count} tenants`
                : `Preview for ${preview.month} — ${preview.count} tenants`}
              <span className="text-[var(--text-muted)] ml-2 text-xs">
                ({lang === "es" ? "fecha emisión" : "issue date"}: {preview.issue_date})
              </span>
            </p>
            {preview.rows.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">
                {lang === "es" ? "No hay tenants elegibles este mes." : "No eligible tenants this month."}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[var(--text-muted)] uppercase tracking-wider">
                        <th className="py-1 pr-2">{lang === "es" ? "Cliente" : "Tenant"}</th>
                        <th className="py-1 pr-2">Plan</th>
                        <th className="py-1 pr-2">{lang === "es" ? "Prorrateo" : "Prorate"}</th>
                        <th className="py-1 pr-2 text-right">{lang === "es" ? "Base sug." : "Suggested"}</th>
                        <th className="py-1 pr-2 text-right">{lang === "es" ? "Base final" : "Final base"}</th>
                        <th className="py-1 pr-2">{lang === "es" ? "Acción" : "Action"}</th>
                        <th className="py-1 pr-2">{lang === "es" ? "Nota" : "Note"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.user_id} className="border-t border-[var(--border-color)]" data-testid={`preview-row-${r.user_id}`}>
                          <td className="py-1 pr-2">
                            <div className="font-medium">{r.tenant_name || r.email}</div>
                            {r.tenant_name && (
                              <div className="text-[10px] text-[var(--text-muted)]">{r.email}</div>
                            )}
                            {r.would_regenerate && (
                              <span className="text-[10px] text-[var(--status-warning)]">
                                {lang === "es" ? "(regenerar)" : "(regenerate)"}
                              </span>
                            )}
                          </td>
                          <td className="py-1 pr-2">
                            <span className="uppercase text-[10px] font-semibold">{r.plan}</span>
                            <div className="text-[10px] text-[var(--text-muted)]">{r.billing_cycle}</div>
                          </td>
                          <td className="py-1 pr-2">
                            {r.prorate?.is_prorated ? (
                              <span className="text-[10px]" data-testid={`prorate-${r.user_id}`}>
                                {r.prorate.days_covered}/{r.prorate.days_in_month}
                                {" "}
                                <span className="text-[var(--text-muted)]">
                                  ({(r.prorate.factor * 100).toFixed(0)}%)
                                </span>
                              </span>
                            ) : (
                              <span className="text-[10px] text-[var(--text-muted)]">—</span>
                            )}
                          </td>
                          <td className="py-1 pr-2 text-right font-mono-num">{fmtEur(r.base)}</td>
                          <td className="py-1 pr-2 text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={r.ui_base}
                              onChange={(e) => updateRow(r.user_id, { ui_base: e.target.value })}
                              className="h-7 w-24 text-right text-xs"
                              disabled={r.ui_action !== "emit"}
                              data-testid={`preview-base-${r.user_id}`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <select
                              value={r.ui_action}
                              onChange={(e) => updateRow(r.user_id, { ui_action: e.target.value })}
                              className="h-7 text-xs rounded border border-[var(--border-color)] bg-[var(--surface)] px-1.5"
                              data-testid={`preview-action-${r.user_id}`}
                            >
                              <option value="emit">{lang === "es" ? "Emitir" : "Emit"}</option>
                              <option value="free">{lang === "es" ? "Gratuito" : "Free"}</option>
                              <option value="exclude">{lang === "es" ? "Excluir" : "Exclude"}</option>
                            </select>
                          </td>
                          <td className="py-1 pr-2">
                            <Input
                              value={r.ui_note}
                              onChange={(e) => updateRow(r.user_id, { ui_note: e.target.value })}
                              placeholder={lang === "es" ? "opcional" : "optional"}
                              className="h-7 w-32 text-xs"
                              data-testid={`preview-note-${r.user_id}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreview(null)}
                    disabled={confirming}
                    data-testid="billing-preview-cancel"
                  >
                    {lang === "es" ? "Cancelar" : "Cancel"}
                  </Button>
                  <Button
                    onClick={confirmEmit}
                    disabled={confirming}
                    data-testid="billing-confirm-btn"
                    className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                  >
                    {confirming ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                    {lang === "es" ? "Confirmar y emitir" : "Confirm & emit"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)] text-xs uppercase tracking-wider">
                <th className="py-2 pr-3">Nº</th>
                <th className="py-2 pr-3">{lang === "es" ? "Fecha" : "Date"}</th>
                <th className="py-2 pr-3">{lang === "es" ? "Cliente" : "Client"}</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3 text-right">Base</th>
                <th className="py-2 pr-3 text-right">IVA</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3"> </th>
              </tr>
            </thead>
            <tbody data-testid="admin-invoices-tbody">
              {loadingList ? (
                <tr><td colSpan={8} className="py-3 text-center text-[var(--text-muted)]">…</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="py-4 text-center text-[var(--text-muted)]">
                  {lang === "es" ? "Sin facturas emitidas este mes." : "No invoices for this month yet."}
                </td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[var(--border-color)] last:border-b-0" data-testid={`admin-invoice-row-${inv.id}`}>
                  <td className="py-2 pr-3 font-mono-num text-[var(--text-secondary)]">{inv.number}</td>
                  <td className="py-2 pr-3">{formatDateShort(inv.issue_date)}</td>
                  <td className="py-2 pr-3">{inv.tenant_name || inv.tenant_email}</td>
                  <td className="py-2 pr-3">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[10px] uppercase font-semibold">{inv.plan}</span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono-num">{fmtEur(inv.base_amount)}</td>
                  <td className="py-2 pr-3 text-right font-mono-num">{fmtEur(inv.vat_amount)}</td>
                  <td className="py-2 pr-3 text-right font-mono-num font-semibold">{fmtEur(inv.total)}</td>
                  <td className="py-2 pr-3 text-right">
                    <button
                      type="button"
                      onClick={() => downloadPdf(inv)}
                      className="text-[var(--primary)] hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`admin-invoice-pdf-${inv.id}`}
                    >
                      <Download size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
